import axios from "axios";

const customerApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "",
  headers: { "Content-Type": "application/json" },
});

// Attach customer JWT + microsite context
customerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("customer_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tid = localStorage.getItem("public_tenant_id");
  const mid = localStorage.getItem("public_microsite_id");
  if (tid) config.headers["X-Tenant-ID"] = tid;
  if (mid) config.headers["X-Microsite-ID"] = mid;
  return config;
});

// Auto-refresh on 401
customerApi.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refreshToken = localStorage.getItem("customer_refresh_token");
      if (refreshToken) {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/account/refresh`,
            { refresh_token: refreshToken },
          );
          const newToken = res.data.access_token;
          localStorage.setItem("customer_token", newToken);
          original.headers.Authorization = `Bearer ${newToken}`;
          return customerApi(original);
        } catch {
          // Refresh failed — logout
        }
      }
      localStorage.removeItem("customer_token");
      localStorage.removeItem("customer_refresh_token");
      localStorage.removeItem("customer_data");
      if (window.location.pathname.startsWith("/account") && window.location.pathname !== "/account/login") {
        window.location.href = "/account/login";
      }
    }
    return Promise.reject(err);
  }
);

// ── Auth (public endpoints — no admin JWT needed) ──
export const customerLogin = (email, password) =>
  customerApi.post("/api/public/account/login", { email, password });

export const customerRegister = (data) =>
  customerApi.post("/api/public/account/register", data);

export const customerRefresh = (refreshToken) =>
  customerApi.post("/api/public/account/refresh", { refresh_token: refreshToken });

export const getCustomerMe = () =>
  customerApi.get("/api/public/account/me");

// ── Customer Portal Data ──
export const getCustomerBookings = () => customerApi.get("/api/public/account/bookings");
export const getCustomerBooking = (id) => customerApi.get(`/api/public/account/bookings/${id}`);
export const getBookingWindowOptions = (id, on) => customerApi.get(`/api/public/account/bookings/${id}/window-options`, { params: on ? { on } : {} });
export const requestWindowChange = (id, data) => customerApi.post(`/api/public/account/bookings/${id}/request-window-change`, data);
export const getCustomerQuotes = () => customerApi.get("/api/public/account/quotes");
export const getCustomerInvoices = () => customerApi.get("/api/public/account/invoices");
export const getCustomerWaivers = () => customerApi.get("/api/public/account/waivers");
export const updateCustomerProfile = (data) => customerApi.patch("/api/public/account/profile", data);

// ── Reviews ──
export const submitReview = (data) => customerApi.post("/api/reviews", data);

// ── Post-Event Damage Report ──
export const reportBookingDamage = (bookingId, damage_details, token) =>
  customerApi.post(
    `/api/public/account/bookings/${bookingId}/report-damage${token ? `?token=${encodeURIComponent(token)}` : ""}`,
    { damage_details },
  );

export default customerApi;
